import React, { useState, useRef, useEffect } from 'react';
import { slideApiService } from '../services/slideApiService';
import { carouselApiService, SearchRefResponse, RecentSearchItem } from '../services/carouselApiService';
import { Slide } from '../types';
import { useShop } from '../contexts/ShopContext';
import SlideCard from './SlideCard';
import CompactSlideCard from './CompactSlideCard';
import SlideEditModal from './SlideEditModal';

interface CarouselSearchTabProps {
  shopId: string;
}

type SearchStatus = 'idle' | 'submitting' | 'processing' | 'loading-slides' | 'done' | 'error';

const CarouselSearchTab: React.FC<CarouselSearchTabProps> = ({ shopId }) => {
  const [prompt, setPrompt] = useState('');
  const [slides, setSlides] = useState<Slide[]>([]);
  const [status, setStatus] = useState<SearchStatus>('idle');
  const [error, setError] = useState('');
  const [searchId, setSearchId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [viewMode, setViewMode] = useState<'standard' | 'compact'>('standard');
  const [editingSlide, setEditingSlide] = useState<Slide | null>(null);
  const [searchMetadata, setSearchMetadata] = useState<SearchRefResponse['metadata'] | null>(null);
  const [reasoningExpanded, setReasoningExpanded] = useState(true);
  const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>([]);
  const [recentSearchesLoaded, setRecentSearchesLoaded] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const pageRef = useRef(page);
  const prevViewMode = useRef(viewMode);
  const { isInternalMember } = useShop();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  // Clear state when shop changes
  useEffect(() => {
    setSlides([]);
    setStatus('idle');
    setError('');
    setSearchId(null);
    setPage(0);
    setTotalElements(0);
    setHasMore(false);
    setSearchMetadata(null);
    setPrompt('');
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, [shopId]);

  // Load recent searches when shop changes
  useEffect(() => {
    setRecentSearchesLoaded(false);
    const loadRecent = async () => {
      try {
        const items = await carouselApiService.getRecentSearches(shopId);
        setRecentSearches(items);
      } catch (err) {
        console.error('Failed to load recent searches:', err);
      }
      setRecentSearchesLoaded(true);
    };
    loadRecent();
  }, [shopId]);

  const pageSize = viewMode === 'compact' ? 60 : 20;

  // Keep pageRef in sync
  useEffect(() => { pageRef.current = page; }, [page]);

  // When view mode toggles, translate the current page position so the user
  // sees the same slides at the top of the view (matches SlidesTab behavior).
  useEffect(() => {
    if (prevViewMode.current === viewMode) return;
    const oldSize = prevViewMode.current === 'compact' ? 60 : 20;
    const newSize = viewMode === 'compact' ? 60 : 20;
    const firstItemIndex = pageRef.current * oldSize;
    const newPage = Math.floor(firstItemIndex / newSize);
    prevViewMode.current = viewMode;

    if (searchId && status === 'done') {
      loadPage(newPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  /**
   * Initiate a new search: POST to carousel-svc, then poll for results.
   */
  const handleSearch = async () => {
    if (!prompt.trim()) return;

    // Reset state for new search
    setSlides([]);
    setError('');
    setSearchMetadata(null);
    setReasoningExpanded(true);
    setPage(0);
    setTotalElements(0);
    setHasMore(false);
    setStatus('submitting');

    try {
      const response = await carouselApiService.initiateSearch(prompt.trim(), shopId);
      setSearchId(response.searchId);
      setStatus('processing');
      pollForResults(response.searchId, 0);
    } catch (err: any) {
      setError(err.message || 'Failed to initiate search');
      setStatus('error');
    }
  };

  /**
   * Poll GET /search/{searchId} until results are ready.
   */
  const pollForResults = (sid: string, pageNum: number) => {
    let attempts = 0;
    const maxAttempts = 40; // 40 x 3s = 120 seconds max
    const intervalMs = 3000;

    const poll = async () => {
      attempts++;
      try {
        const result = await carouselApiService.getSearchResults(sid, pageNum, pageSize);

        if (result.status === 200) {
          const data = result.data as SearchRefResponse;
          setSearchMetadata(data.metadata);
          await hydrateSlides(data, pageNum);
          return;
        }

        // Still processing — poll again
        if (attempts < maxAttempts) {
          pollingRef.current = setTimeout(poll, intervalMs);
        } else {
          setError('Search is taking longer than expected. Please try again.');
          setStatus('error');
        }
      } catch (err: any) {
        setError(err.message || 'Search failed');
        setStatus('error');
      }
    };

    pollingRef.current = setTimeout(poll, intervalMs);
  };

  /**
   * Hydrate the search result refs into full slides. Internal members hydrate from slide-svc
   * (batch); external (CLIENT/VENDOR) members hydrate from carousel-svc /hydrate (role-stripped),
   * because 4b denies them slide-svc's slide-data endpoints.
   */
  const hydrateSlides = async (refResponse: SearchRefResponse, pageNum: number) => {
    setStatus('loading-slides');

    try {
      const slideIds = refResponse.items.map(ref => ref.id);

      if (slideIds.length === 0) {
        setSlides([]);
        setTotalElements(refResponse.pagination.totalElements);
        setHasMore(false);
        setPage(pageNum);
        setStatus('done');
        return;
      }

      const hydratedSlides = isInternalMember
        ? await slideApiService.batchGetSlides(shopId, slideIds)
        : await carouselApiService.hydrateSlides(
            refResponse.items.map(ref => ({ slideId: ref.id, shopId: ref.shopId })));

      setSlides(hydratedSlides);
      setTotalElements(refResponse.pagination.totalElements);
      setHasMore(pageNum < refResponse.pagination.totalPages - 1);
      setPage(pageNum);
      setStatus('done');

      // Refresh recent searches so the list stays current
      carouselApiService.getRecentSearches(shopId)
        .then(items => setRecentSearches(items))
        .catch(() => {});
    } catch (err: any) {
      setError(err.message || 'Failed to load slide details');
      setStatus('error');
    }
  };

  /**
   * Load a different page from an existing search session.
   */
  const loadPage = async (pageNum: number) => {
    if (!searchId) return;

    setStatus('loading-slides');
    setError('');

    try {
      const result = await carouselApiService.getSearchResults(searchId, pageNum, pageSize);

      if (result.status === 200) {
        await hydrateSlides(result.data as SearchRefResponse, pageNum);
      } else {
        setError('Search results are no longer available. Please start a new search.');
        setStatus('error');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load page');
      setStatus('error');
    }
  };

  const handleSlideUpdated = (updatedSlide: Slide) => {
    setSlides(prev => prev.map(s => s.id === updatedSlide.id ? updatedSlide : s));
  };

  const handleSlideDeleted = (slideId: string) => {
    setSlides(prev => prev.filter(s => s.id !== slideId));
  };

  const handleSlideEdit = (slide: Slide) => {
    const freshSlide = slides.find(s => s.id === slide.id) || slide;
    setEditingSlide(freshSlide);
  };

  const isSearching = status === 'submitting' || status === 'processing' || status === 'loading-slides';

  return (
    <div className="carousel-search-tab">
      {/* Search Input */}
      <div className="card mb-6">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Row 1: Prompt + View Toggle */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
              <textarea
                placeholder="Describe what you're looking for... (e.g., &quot;Show me vintage holiday items&quot;)"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                disabled={isSearching}
                style={{
                  width: '100%',
                  minHeight: '60px',
                  maxHeight: '120px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  transition: 'border-color 0.15s ease',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--primary-light)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-light)'; }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexShrink: 0 }}>
              <button
                onClick={handleSearch}
                disabled={!prompt.trim() || isSearching}
                className="btn btn-primary"
                style={{
                  padding: '10px 20px',
                  opacity: !prompt.trim() || isSearching ? 0.5 : 1,
                  cursor: !prompt.trim() || isSearching ? 'not-allowed' : 'pointer',
                }}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>

              {/* View Mode Toggle */}
              <div style={{ display: 'flex', border: '1px solid var(--border-medium)', borderRadius: '6px', overflow: 'hidden' }}>
                <button
                  onClick={() => setViewMode('standard')}
                  title="Standard view"
                  style={{
                    padding: '6px 10px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: viewMode === 'standard' ? 'var(--delft-blue)' : 'var(--bg-card)',
                    color: viewMode === 'standard' ? 'var(--text-inverse)' : 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" rx="1"/>
                    <rect x="14" y="3" width="7" height="7" rx="1"/>
                    <rect x="3" y="14" width="7" height="7" rx="1"/>
                    <rect x="14" y="14" width="7" height="7" rx="1"/>
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('compact')}
                  title="Compact view"
                  style={{
                    padding: '6px 10px',
                    border: 'none',
                    borderLeft: '1px solid var(--border-medium)',
                    cursor: 'pointer',
                    backgroundColor: viewMode === 'compact' ? 'var(--delft-blue)' : 'var(--bg-card)',
                    color: viewMode === 'compact' ? 'var(--text-inverse)' : 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="5" height="5" rx="0.5"/>
                    <rect x="9.5" y="2" width="5" height="5" rx="0.5"/>
                    <rect x="17" y="2" width="5" height="5" rx="0.5"/>
                    <rect x="2" y="9.5" width="5" height="5" rx="0.5"/>
                    <rect x="9.5" y="9.5" width="5" height="5" rx="0.5"/>
                    <rect x="17" y="9.5" width="5" height="5" rx="0.5"/>
                    <rect x="2" y="17" width="5" height="5" rx="0.5"/>
                    <rect x="9.5" y="17" width="5" height="5" rx="0.5"/>
                    <rect x="17" y="17" width="5" height="5" rx="0.5"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Processing Indicator */}
      {(status === 'submitting' || status === 'processing') && (
        <div className="card text-center py-12">
          <div className="spinner-sm mx-auto mb-2"></div>
          <p className="text-muted" style={{ margin: 0 }}>
            {status === 'submitting' ? 'Submitting search...' : 'AI is analyzing your prompt...'}
          </p>
          <p className="text-muted" style={{ margin: '8px 0 0', fontSize: '0.8125rem' }}>
            This typically takes 5-10 seconds
          </p>
        </div>
      )}

      {/* Loading Slides Indicator */}
      {status === 'loading-slides' && (
        <div className="card text-center py-12">
          <div className="spinner-sm mx-auto mb-2"></div>
          <p className="text-muted" style={{ margin: 0 }}>Loading slide details...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="error-banner card mb-6">
          <div className="text-error">{error}</div>
        </div>
      )}

      {/* Empty State — before any search */}
      {status === 'idle' && slides.length === 0 && recentSearchesLoaded && (
        <div className="empty-state card text-center py-12">
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔍</div>
          <h3 className="heading-secondary" style={{ fontSize: '1.125rem', marginBottom: '8px' }}>
            Intelligent Search
          </h3>
          {recentSearches.length > 0 ? (
            <div style={{ maxWidth: '400px', margin: '0 auto' }}>
              <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '8px' }}>Recent Searches</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                {recentSearches.map((search) => (
                  <button
                    key={search.prompt}
                    onClick={() => { setPrompt(search.prompt); }}
                    onDoubleClick={() => { setPrompt(search.prompt); setTimeout(handleSearch, 0); }}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      border: '1px solid var(--border-light)',
                      backgroundColor: 'var(--bg-accent)',
                      color: 'var(--text-accent)',
                      fontSize: '0.8125rem',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    title="Click to fill, double-click to search"
                  >
                    {search.prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <p className="text-muted" style={{ maxWidth: '400px', margin: '0 auto 16px' }}>
                Describe what you're looking for using natural language. AI will match your prompt against your inventory categories.
              </p>
              <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '8px' }}>Try these examples</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                  {['vintage brass candlestick', 'blue and white pottery', 'art deco jewelry'].map((text) => (
                    <button
                      key={text}
                      onClick={() => { setPrompt(text); }}
                      onDoubleClick={() => { setPrompt(text); setTimeout(handleSearch, 0); }}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: '1px solid var(--border-light)',
                        backgroundColor: 'var(--bg-accent)',
                        color: 'var(--text-accent)',
                        fontSize: '0.8125rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      title="Click to fill, double-click to search"
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* No Results */}
      {status === 'done' && slides.length === 0 && (
        <div className="empty-state card text-center py-12">
          <h3 className="heading-secondary" style={{ fontSize: '1.125rem', marginBottom: '8px' }}>
            No Matching Slides
          </h3>
          <p className="text-muted">Try rephrasing your search or using different terms.</p>
        </div>
      )}

      {/* AI Reasoning */}
      {status === 'done' && searchMetadata?.reasoning && (
        <div
          onClick={() => setReasoningExpanded(!reasoningExpanded)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '10px 14px',
            marginBottom: '16px',
            backgroundColor: 'var(--delft-frost, var(--bg-secondary))',
            borderRadius: '8px',
            fontSize: '0.875rem',
            color: 'var(--charcoal, var(--text-primary))',
            lineHeight: '1.5',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
            <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="var(--delft-blue, #1B3A5C)"/>
          </svg>
          <span style={{
            flex: 1,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: reasoningExpanded ? 'unset' : 1,
            WebkitBoxOrient: 'vertical',
          } as React.CSSProperties}>
            {searchMetadata.reasoning}
          </span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }}>
            {reasoningExpanded ? '\u25B2' : '\u25BC'}
          </span>
        </div>
      )}

      {/* Results Grid */}
      {status === 'done' && slides.length > 0 && (
        <>
          {viewMode === 'compact' ? (
            <div className="slides-grid-compact">
              {slides.map((slide) => (
                <div key={slide.id}>
                  <CompactSlideCard
                    slide={slide}
                    onSlideUpdated={handleSlideUpdated}
                    onSlideDeleted={handleSlideDeleted}
                    onSlideEdited={handleSlideEdit}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="slides-grid">
              {slides.map((slide) => (
                <div key={slide.id}>
                  <SlideCard
                    slide={slide}
                    onSlideUpdated={handleSlideUpdated}
                    onSlideDeleted={handleSlideDeleted}
                    onSlideEdited={handleSlideEdit}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            padding: '24px 0',
            borderTop: '1px solid var(--border-light)',
            marginTop: '24px'
          }}>
            <p className="text-muted" style={{ fontSize: '0.875rem', margin: 0 }}>
              Showing slides {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalElements)} of {totalElements}
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  loadPage(page - 1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={page === 0 || isSearching}
                className="btn btn-secondary"
                style={{
                  opacity: page === 0 || isSearching ? 0.5 : 1,
                  cursor: page === 0 || isSearching ? 'not-allowed' : 'pointer'
                }}
              >
                &larr; Previous Page
              </button>

              <button
                onClick={() => {
                  loadPage(page + 1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={!hasMore || isSearching}
                className="btn btn-secondary"
                style={{
                  opacity: !hasMore || isSearching ? 0.5 : 1,
                  cursor: !hasMore || isSearching ? 'not-allowed' : 'pointer'
                }}
              >
                Next Page &rarr;
              </button>
            </div>
          </div>
        </>
      )}

      {/* Edit Slide Modal */}
      {editingSlide && (
        <SlideEditModal
          slide={editingSlide}
          onClose={() => setEditingSlide(null)}
          onSlideUpdated={handleSlideUpdated}
        />
      )}
    </div>
  );
};

export default CarouselSearchTab;
